import _ from "lodash";

import {
  AppConfigProvider,
  AppConfigService,
  HttpService,
  UrlType,
} from "@sportal/api";
import { EULA_CANCEL_STRATEGY } from "../../pages/login/components";
import {
  AppConfig,
  ExternalLink,
  Language,
  Weekdays,
  UrlFilters,
} from "./appConfigService.types";

export const defaultLanguage: Language = {
  key: "us_EG",
  name: "English",
  inversedDirection: false,
};

const defaults: Partial<AppConfig> = {
  emailRegex: '^([^\\s"@]+|(?:"[^"]+"))@([^.\\s@]+\\.)+[^.\\s@]{2,63}$',
  security: {
    noLoginPage: false,
    responseCodeHeader: "X-Response-Code",
    urlParamsForSubscriberData: ["id", "name", "mode"],
    logoutUrl: "",
  },
  firstDayOfWeek: Weekdays.Monday,
  helpPageLink: "#/help",
  logoLink: "/home",
  footerCompanyLink: "https://akamai.com",
  useFallbackLanguage: true,
  urlFilters: {
    "url-types": [UrlType.CORE_DOMAIN],
  },
  secureBlocksLimit: 50000,
  secureBlocksLimitCsv: 500000,
  customLinkRef: "",
  showScheduledReports: true,
  showDrilldownPeriod: true,
  appTitle: "Secure Internet Access SMB",
  showEulaHeader: false,
  showEulaFooter: false,
  showEulaDialog: false,
  eulaCancelStrategy: EULA_CANCEL_STRATEGY.RELOAD,
  eulaCancelRedirectUrl: "",
  reportingTimeoutMinutes: 0,
  // These options are only for one customer
  // Please do not add this properties to appConfig.json.example
  footerIcons: [],
  footerPhoneNumbers: [],
  footerSupportLink: "",
  loginLink: "#/login",
};

const isLinkExternal = (link: string): boolean => /^https?:\/\//i.test(link);

export const mergeLink = (
  defaultValue: string | ExternalLink,
  configValue: string
): ExternalLink => {
  const isExternal = isLinkExternal(configValue);

  if (_.isObject(defaultValue)) {
    return {
      isExternal: isExternal || defaultValue.isExternal,
      url: isExternal ? configValue : defaultValue.url,
    };
  }

  return {
    isExternal,
    url: isExternal ? configValue : defaultValue || configValue,
  };
};

export const mergeUrl = (defaultValue: string, configValue: string): string =>
  configValue && isLinkExternal(configValue) ? configValue : defaultValue;

export const mergeLimit = (defaultValue: number, configValue: number): number =>
  _.isInteger(configValue) && configValue > 0 ? configValue : defaultValue;

export const mergeString = (
  defaultValue: string,
  configValue: string
): string => configValue || defaultValue;

const allowedUrlFilters = (
  defaultOptions: UrlFilters,
  configOptions: UrlFilters
) => {
  const allowedTypes = ["core-domain", "fqdn", "public-suffix"];
  const config = configOptions || defaultOptions;

  return {
    ...config,
    "url-types": config["url-types"].filter((url) =>
      allowedTypes.includes(url)
    ),
  };
};

const customizers = {
  helpPageLink: mergeLink,
  logoLink: mergeLink,
  footerSupportLink: mergeLink,
  footerCompanyLink: mergeLink,
  logoutUrl: mergeUrl,
  secureBlocksLimit: mergeLimit,
  secureBlocksLimitCsv: mergeLimit,
  appTitle: mergeString,
  eulaCancelRedirectUrl: mergeUrl,
  loginLink: mergeUrl,
  urlFilters: allowedUrlFilters,
};

export const makeAppConfigService = (
  http: HttpService
): AppConfigService<AppConfig> =>
  new AppConfigService(defaults, customizers, new AppConfigProvider(http));
